import { useState, useMemo, useCallback } from "react";
import "./ArticleTreeView.css";

const defaultCanCheck = () => true;

const ArticleTreeView = ({
  articles,
  groupBy,
  search,
  selectedArticle,
  onSelect,
  checkedIds,
  onCheckedIdsChange,
  canCheck = defaultCanCheck,
  checkboxDisabledTitle = "Not available",
  renderBadge,
}) => {
  const [collapsed, setCollapsed] = useState({});

  const toggleGroup = (key) => {
    setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return articles.filter(
      (a) =>
        a.name?.toLowerCase().includes(q) ||
        a.manufacturer?.toLowerCase().includes(q) ||
        a.type?.toLowerCase().includes(q) ||
        a.articleNumber?.toLowerCase().includes(q)
    );
  }, [articles, search]);

  const grouped = useMemo(() => {
    return filtered.reduce((acc, article) => {
      let key;
      switch (groupBy) {
        case "type":
          key = article.type || "Unknown";
          break;
        case "recordType":
          key = article.recordType || "Unknown";
          break;
        case "name":
          key = article.name || "Unknown";
          break;
        default:
          key = article.manufacturer || "Unknown";
          break;
      }
      (acc[key] ??= []).push(article);
      return acc;
    }, {});
  }, [filtered, groupBy]);

  const toggleItem = useCallback(
    (id) => {
      const next = new Set(checkedIds);
      next.has(id) ? next.delete(id) : next.add(id);
      onCheckedIdsChange(next);
    },
    [checkedIds, onCheckedIdsChange]
  );

  const toggleGroupSelection = useCallback(
    (items, checked) => {
      const next = new Set(checkedIds);
      items.forEach((a) => {
        if (!canCheck(a)) return;
        checked ? next.add(a.id) : next.delete(a.id);
      });
      onCheckedIdsChange(next);
    },
    [checkedIds, onCheckedIdsChange, canCheck]
  );

  const getGroupState = (items) => {
    const selectable = items.filter((a) => canCheck(a));
    if (selectable.length === 0) return "disabled";
    const checkedCount = selectable.filter((a) => checkedIds.has(a.id)).length;
    if (checkedCount === 0) return "unchecked";
    if (checkedCount === selectable.length) return "checked";
    return "indeterminate";
  };

  return (
    <div className="article-tree">
      {Object.entries(grouped).map(([group, items]) => {
        const isCollapsed = collapsed[group];
        const groupState = getGroupState(items);

        return (
          <div key={group} className="tree-group">
            <div className={`tree-group-title ${isCollapsed ? "collapsed" : ""}`}>
              <span className="tree-arrow" onClick={() => toggleGroup(group)} />
              <input
                type="checkbox"
                className="tree-checkbox"
                checked={groupState === "checked"}
                ref={(el) => {
                  if (el) el.indeterminate = groupState === "indeterminate";
                }}
                disabled={groupState === "disabled"}
                onChange={(e) => toggleGroupSelection(items, e.target.checked)}
                onClick={(e) => e.stopPropagation()}
              />
              <span className="tree-icon tree-icon-folder" />
              <span className="tree-label" onClick={() => toggleGroup(group)}>
                {group}
              </span>
            </div>

            {!isCollapsed && (
              <div className="tree-children">
                {items.map((article) => {
                  const checkable = canCheck(article);

                  return (
                    <div
                      key={article.id}
                      className={`tree-item ${
                        selectedArticle?.id === article.id ? "selected" : ""
                      }`}
                      onClick={() => onSelect(article)}
                    >
                      <span className="tree-arrow-spacer" />
                      <input
                        type="checkbox"
                        className="tree-checkbox"
                        checked={checkedIds.has(article.id)}
                        disabled={!checkable}
                        title={!checkable ? checkboxDisabledTitle : undefined}
                        onChange={() => toggleItem(article.id)}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <span className="tree-icon-wrap">
                        <span className="tree-icon tree-icon-item" />
                        {renderBadge?.(article)}
                      </span>
                      <span className="tree-label">{article.name}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default ArticleTreeView;